import React from "react";
import propTypes from "prop-types";

import useAdminParams from "../hooks/useAdminParams";
import { generatePath } from "react-router-dom";
import { useAdmin } from "providers/AdminProvider/AdminProvider";

import Navbar from "react-bootstrap/cjs/Navbar";
import Nav from "react-bootstrap/cjs/Nav";
import NavDropdown from "react-bootstrap/cjs/NavDropdown";
import ScrollableNavDropdown from "../components/ScrollableDropdown/ScrollableNavDropdown";
import NavbarLink from "../components/NavbarLink/NavbarLink";
import NavDropdownItem from "../components/NavDropdownItem/NavDropdownItem";
import Button from "react-bootstrap/cjs/Button";
import Loader from "components/Loader/Loader";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkAlt";

import * as routes from "routes";
import { useFlags } from "launchdarkly-react-client-sdk";

const PortalNavigation = ({ loading, courses, licensed }) => {
  const { ilx58 } = useFlags();

  const { portalId, courseId: currentCourseId } = useAdminParams();

  const { globalAdmin, enrolmentAdmin, instanceAdmin, portal, pcat } = useAdmin();
  const { showCustomItems = 0, showCustomResources = 0, portalUrl } = portal ?? {};

  const portalAdmin = globalAdmin || enrolmentAdmin || instanceAdmin;

  return (
    <Navbar bg={"light"} variant={"light"} className={"mb-2 clearfix"}>
      <Nav className={"mr-auto"}>
        <NavbarLink to={generatePath(routes.PORTAL_ID, { portalId })}>Summary</NavbarLink>
        {portalAdmin && (
          <NavDropdown id={"users"} title={"Users"}>
            <NavDropdownItem exact to={generatePath(routes.PORTAL_USERS, { portalId })}>
              List
            </NavDropdownItem>

            <NavDropdownItem exact to={generatePath(routes.PORTAL_ADD_USER, { portalId })}>
              Add User
            </NavDropdownItem>

            <NavDropdownItem exact to={generatePath(routes.PORTAL_IMPORT_USERS, { portalId })}>
              Import Users
            </NavDropdownItem>

            {enrolmentAdmin && (portal?.enrolmentRequests || portal?.enableCourseEnrolment) ? (
              <>
                <NavDropdown.Divider />

                <NavDropdownItem exact to={generatePath(routes.PORTAL_USERS_ENROL, { portalId })}>
                  Enrolment Requests
                </NavDropdownItem>
              </>
            ) : null}
          </NavDropdown>
        )}
        <ScrollableNavDropdown id={"reports"} title={"Reports"}>
          {loading && <Loader />}
          {ilx58 && !loading && pcat && (
            <>
              <NavDropdownItem to={generatePath(routes.PORTAL_PCAT_REPORT, { portalId })}>
                PCAT
              </NavDropdownItem>
              <NavDropdown.Divider />
            </>
          )}
          {!loading && Array.isArray(courses) && !courses.length && (
            <NavDropdownItem to={generatePath(routes.PORTAL_ID, { portalId })} disabled>
              <em>No course reports available</em>
            </NavDropdownItem>
          )}
          {!loading && Array.isArray(courses) && !!courses.length && (
            <>
              <NavDropdownItem
                exact
                to={generatePath(routes.PORTAL_COURSE_REPORT, {
                  portalId,
                  tab: "summary",
                  courseId: "all",
                  licenceId: "overview",
                })}
                isActive={currentCourseId === "all"}
              >
                All Courses
              </NavDropdownItem>
              <NavDropdown.Divider />
              {courses.map(({ name, courseId }) => (
                <NavDropdownItem
                  key={courseId}
                  to={generatePath(routes.PORTAL_COURSE_REPORT, {
                    portalId,
                    tab: "summary",
                    courseId: courseId,
                    licenceId: "overview",
                  })}
                  isActive={courseId === currentCourseId}
                >
                  {name}
                </NavDropdownItem>
              ))}
            </>
          )}
        </ScrollableNavDropdown>
        {portalAdmin && (
          <NavDropdown id={"configure"} title={"Configure"}>
            {(portalAdmin || !!showCustomItems) && (
              <NavDropdownItem
                exact
                to={generatePath(routes.PORTAL_CUSTOMISEABLEITEMS, { portalId })}
              >
                Customisable Items
              </NavDropdownItem>
            )}
            {(portalAdmin || !!showCustomResources) && (
              <NavDropdownItem exact to={generatePath(routes.PORTAL_RESOURCES, { portalId })}>
                Resources
              </NavDropdownItem>
            )}
            {globalAdmin && !!licensed && (
              <NavDropdownItem exact to={generatePath(routes.PORTAL_LICENCES, { portalId })}>
                Licensing
              </NavDropdownItem>
            )}
            {globalAdmin && (
              <NavDropdownItem to={generatePath(routes.PORTAL_SETTINGS, { portalId })}>
                Settings
              </NavDropdownItem>
            )}
          </NavDropdown>
        )}
      </Nav>
      {!!URL && (
        <Button href={portalUrl} target={"_blank"}>
          Launch Portal <FontAwesomeIcon icon={faExternalLinkAlt} className={"ml-4"} />
        </Button>
      )}
    </Navbar>
  );
};

PortalNavigation.propTypes = {
  loading: propTypes.bool,
  courses: propTypes.arrayOf(
    propTypes.shape({
      name: propTypes.string.isRequired,
      courseId: propTypes.oneOfType([propTypes.number, propTypes.string]).isRequired,
    })
  ).isRequired,
  licensed: propTypes.bool.isRequired,
};

PortalNavigation.defaultProps = {
  loading: true,
  courses: [],
  licensed: false,
};

export default PortalNavigation;
